// src/components/admin/CompletedOrders.jsx

import React, {
    useEffect,
    useState,
} from "react";

import {
    Box,
    Grid,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    TextField,
    IconButton,
    Alert,
    Pagination,
    MenuItem,
} from "@mui/material";

import DeleteIcon from
    "@mui/icons-material/Delete";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    setStatus,
} from "../../features/ordersSlice";

import {
    GetCompletedOrdersPaged,
    RemoveOrder,
} from "../../utils/order";

import {
    GetAllStatus,
} from "../../utils/lookupUtil";

import AdminEditProductDialog from
    "./AdminEditProductDialog";

/*
 * מחזיר את החודש הנוכחי בפורמט:
 * 2026-08
 */
const getCurrentMonth = () => {
    const currentDate = new Date();

    const year =
        currentDate.getFullYear();

    const month =
        String(
            currentDate.getMonth() + 1
        ).padStart(2, "0");

    return `${year}-${month}`;
};

const CompletedOrders = () => {
    const dispatch = useDispatch();

    /*
     * הסטטוסים משמשים את חלונית
     * הצפייה והעריכה של ההזמנה.
     */
    const status = useSelector(
        (state) => state.orders.status
    );

    /*
     * ההזמנות שמתקבלות מהשרת
     * עבור החודש והעמוד שנבחרו.
     */
    const [
        completedOrders,
        setCompletedOrders,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        error,
        setError,
    ] = useState("");

    /*
     * חלונית צפייה ועריכת הזמנה.
     */
    const [
        open,
        setOpen,
    ] = useState(false);

    const [
        orderDetails,
        setOrderDetails,
    ] = useState(null);

    /*
     * ברירת המחדל היא החודש הנוכחי.
     */
    const [
        selectedMonth,
        setSelectedMonth,
    ] = useState(getCurrentMonth);

    /*
     * הטקסט שנמצא בשדה החיפוש.
     */
    const [
        nameInput,
        setNameInput,
    ] = useState("");

    /*
     * שם הלקוח שנשלח בפועל לשרת.
     */
    const [
        customerName,
        setCustomerName,
    ] = useState("");

    /*
     * React מתחיל מעמוד 0.
     * השרת מתחיל מעמוד 1.
     */
    const [
        page,
        setPage,
    ] = useState(0);

    const [
        rowsPerPage,
        setRowsPerPage,
    ] = useState(20);

    const [
        totalOrders,
        setTotalOrders,
    ] = useState(0);

    /*
     * הפיכת 2026-08 לשנה ולחודש.
     */
    const [
        selectedYear,
        selectedMonthNumber,
    ] = selectedMonth
        .split("-")
        .map(Number);

    /*
     * מספר העמודים הכולל.
     */
    const totalPages = Math.max(
        1,
        Math.ceil(
            totalOrders / rowsPerPage
        )
    );

    /*
     * מספר ההזמנה הראשונה
     * שמוצגת בעמוד הנוכחי.
     */
    const firstDisplayedOrder =
        totalOrders === 0
            ? 0
            : page * rowsPerPage + 1;

    /*
     * מספר ההזמנה האחרונה
     * שמוצגת בעמוד הנוכחי.
     */
    const lastDisplayedOrder =
        Math.min(
            (page + 1) * rowsPerPage,
            totalOrders
        );

    /*
     * טעינת הסטטוסים עבור
     * חלונית עריכת ההזמנה.
     */
    useEffect(() => {
        const loadStatuses = async () => {
            if (
                status &&
                status.length > 0
            ) {
                return;
            }

            try {
                const data =
                    await GetAllStatus();

                dispatch(
                    setStatus(data)
                );
            } catch (err) {
                console.error(
                    "שגיאה בטעינת סטטוסים:",
                    err
                );
            }
        };

        loadStatuses();
    }, [dispatch, status]);

    /*
     * טעינת הזמנות שבוצעו מהשרת.
     */
    const loadCompletedOrders =
        async () => {
            try {
                setLoading(true);
                setError("");

                const data =
                    await GetCompletedOrdersPaged({
                        year: selectedYear,

                        month:
                            selectedMonthNumber,

                        /*
                         * השרת מתחיל מעמוד 1.
                         */
                        page: page + 1,

                        pageSize:
                            rowsPerPage,

                        customerName,
                    });

                setCompletedOrders(
                    data.items || []
                );

                setTotalOrders(
                    Number(data.total || 0)
                );
            } catch (err) {
                console.error(
                    "שגיאה בטעינת ההזמנות:",
                    err
                );

                setCompletedOrders([]);
                setTotalOrders(0);

                if (
                    err?.response?.status === 401
                ) {
                    setError(
                        "אין הרשאה לצפות בהזמנות. יש להתחבר מחדש כמנהל."
                    );
                } else if (
                    err?.response?.status === 403
                ) {
                    setError(
                        "המשתמש המחובר אינו מנהל."
                    );
                } else {
                    setError(
                        err?.response?.data
                            ?.message ||
                        "אירעה שגיאה בטעינת ההזמנות."
                    );
                }
            } finally {
                setLoading(false);
            }
        };

    /*
     * פנייה חדשה לשרת בכל שינוי
     * של חודש, עמוד, כמות או חיפוש.
     */
    useEffect(() => {
        loadCompletedOrders();
    }, [
        selectedYear,
        selectedMonthNumber,
        page,
        rowsPerPage,
        customerName,
    ]);

    const handleMonthChange = (
        event
    ) => {
        setSelectedMonth(
            event.target.value
        );

        /*
         * כשמשנים חודש חוזרים
         * לעמוד הראשון.
         */
        setPage(0);
    };

    const handleSearch = () => {
        setCustomerName(
            nameInput.trim()
        );

        setPage(0);
    };

    const handleSearchKeyDown = (
        event
    ) => {
        if (event.key === "Enter") {
            handleSearch();
        }
    };

    const handleClearSearch = () => {
        setNameInput("");
        setCustomerName("");
        setPage(0);
    };

    const handleChangeRowsPerPage = (
        event
    ) => {
        setRowsPerPage(
            Number(event.target.value)
        );

        setPage(0);
    };

    const handleDetailsOrder = (
        order
    ) => {
        setOrderDetails(order);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setOrderDetails(null);

        /*
         * טעינת הרשימה מחדש
         * לאחר סגירת חלונית העריכה.
         */
        loadCompletedOrders();
    };

    const handleDeleteOrder =
        async (orderId) => {
            const confirmDelete =
                window.confirm(
                    "האם את בטוחה שברצונך למחוק את ההזמנה?"
                );

            if (!confirmDelete) {
                return;
            }

            try {
                await RemoveOrder(orderId);

                /*
                 * אם נמחקה ההזמנה היחידה
                 * בעמוד שאינו הראשון,
                 * חוזרים לעמוד הקודם.
                 */
                if (
                    completedOrders.length === 1 &&
                    page > 0
                ) {
                    setPage(
                        (previousPage) =>
                            previousPage - 1
                    );
                } else {
                    await loadCompletedOrders();
                }
            } catch (err) {
                console.error(
                    "שגיאה במחיקת הזמנה:",
                    err
                );

                alert(
                    err?.response?.data
                        ?.message ||
                    "שגיאה במחיקת ההזמנה"
                );
            }
        };

    return (
        <Box
            dir="rtl"
            sx={{
                flexGrow: 1,
                padding: 3,
            }}
        >
            {open && orderDetails && (
                <AdminEditProductDialog
                    open={open}
                    handleClose={handleClose}
                    order={orderDetails}
                />
            )}

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper
                        sx={{
                            padding: 2,
                        }}
                    >
                        <Typography
                            variant="h4"
                            gutterBottom
                        >
                            הזמנות שבוצעו
                        </Typography>

                        {/* בחירת חודש וחיפוש */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                mt: 2,
                                mb: 2,
                                alignItems:
                                    "flex-start",
                                flexDirection: {
                                    xs: "column",
                                    md: "row",
                                },
                            }}
                        >
                            <TextField
                                label="בחירת חודש"
                                type="month"
                                value={
                                    selectedMonth
                                }
                                onChange={
                                    handleMonthChange
                                }
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    /*
                                     * לא מאפשר בחירת
                                     * חודש עתידי.
                                     */
                                    max:
                                        getCurrentMonth(),
                                }}
                                sx={{
                                    minWidth: {
                                        xs: "100%",
                                        md: 220,
                                    },
                                }}
                            />

                            <TextField
                                label="חיפוש לפי שם לקוח"
                                value={nameInput}
                                onChange={(event) =>
                                    setNameInput(
                                        event.target
                                            .value
                                    )
                                }
                                onKeyDown={
                                    handleSearchKeyDown
                                }
                                fullWidth
                            />

                            <Button
                                variant="contained"
                                onClick={
                                    handleSearch
                                }
                                sx={{
                                    minWidth: 110,
                                    height: 56,
                                }}
                            >
                                חיפוש
                            </Button>

                            <Button
                                variant="outlined"
                                color="error"
                                onClick={
                                    handleClearSearch
                                }
                                disabled={
                                    !nameInput &&
                                    !customerName
                                }
                                sx={{
                                    minWidth: 110,
                                    height: 56,
                                }}
                            >
                                ניקוי
                            </Button>
                        </Box>

                        {/* הודעת שגיאה */}
                        {error && (
                            <Alert
                                severity="error"
                                sx={{ mb: 2 }}
                            >
                                {error}
                            </Alert>
                        )}

                        {/* טעינה */}
                        {loading ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                sx={{
                                    p: 5,
                                    gap: 2,
                                }}
                            >
                                <CircularProgress
                                    size={30}
                                />

                                <Typography>
                                    טוען הזמנות
                                    שבוצעו...
                                </Typography>
                            </Box>
                        ) : (
                            <TableContainer
                                component={Paper}
                                variant="outlined"
                                sx={{
                                    marginTop: 2,
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                שם לקוח
                                            </TableCell>

                                            <TableCell>
                                                תאריך
                                            </TableCell>

                                            <TableCell>
                                                סה״כ
                                            </TableCell>

                                            <TableCell>
                                                לצפייה בהזמנה
                                            </TableCell>

                                            <TableCell>
                                                מחיקה
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {completedOrders.map(
                                            (order) => (
                                                <TableRow
                                                    key={
                                                        order.id
                                                    }
                                                    hover
                                                >
                                                    <TableCell>
                                                        {order
                                                            .user
                                                            ?.name ||
                                                            "לא ידוע"}
                                                    </TableCell>

                                                    <TableCell>
                                                        {
                                                            order.date
                                                        }
                                                    </TableCell>

                                                    <TableCell>
                                                        {Number(
                                                            order.totalCost ||
                                                            0
                                                        ).toFixed(
                                                            2
                                                        )}{" "}
                                                        ₪
                                                    </TableCell>

                                                    <TableCell>
                                                        <Button
                                                            variant="outlined"
                                                            color="secondary"
                                                            onClick={() =>
                                                                handleDetailsOrder(
                                                                    order
                                                                )
                                                            }
                                                        >
                                                            לצפייה
                                                            בהזמנה
                                                        </Button>
                                                    </TableCell>

                                                    <TableCell>
                                                        <IconButton
                                                            color="error"
                                                            aria-label={`מחיקת הזמנה ${order.id}`}
                                                            onClick={() =>
                                                                handleDeleteOrder(
                                                                    order.id
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}

                                        {completedOrders.length ===
                                            0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={
                                                        5
                                                    }
                                                    align="center"
                                                    sx={{
                                                        py: 5,
                                                    }}
                                                >
                                                    אין הזמנות
                                                    שבוצעו בחודש
                                                    שנבחר
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>

                                {/* אזור החלוקה לעמודים */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "space-between",
                                        gap: 3,
                                        px: 2,
                                        py: 2,
                                        borderTop: 1,
                                        borderColor:
                                            "divider",
                                        backgroundColor:
                                            "#fafafa",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    {/* מספר ההזמנות */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight:
                                                600,
                                        }}
                                    >
                                        מציג הזמנות{" "}
                                        {
                                            firstDisplayedOrder
                                        }
                                        –
                                        {
                                            lastDisplayedOrder
                                        }{" "}
                                        מתוך{" "}
                                        {totalOrders}
                                    </Typography>

                                    {/* בחירת כמות בעמוד */}
                                    <TextField
                                        select
                                        size="small"
                                        label="הזמנות בעמוד"
                                        value={
                                            rowsPerPage
                                        }
                                        onChange={
                                            handleChangeRowsPerPage
                                        }
                                        sx={{
                                            minWidth:
                                                150,
                                        }}
                                    >
                                        <MenuItem
                                            value={10}
                                        >
                                            10
                                        </MenuItem>

                                        <MenuItem
                                            value={20}
                                        >
                                            20
                                        </MenuItem>

                                        <MenuItem
                                            value={50}
                                        >
                                            50
                                        </MenuItem>
                                    </TextField>

                                    {/* מספרי עמודים */}
                                    {totalOrders > 0 && (
                                        <Box
                                            dir="ltr"
                                            sx={{
                                                display:
                                                    "flex",
                                                justifyContent:
                                                    "center",
                                            }}
                                        >
                                            <Pagination
                                                count={
                                                    totalPages
                                                }
                                                page={
                                                    page +
                                                    1
                                                }
                                                onChange={(
                                                    event,
                                                    selectedPage
                                                ) => {
                                                    setPage(
                                                        selectedPage -
                                                            1
                                                    );
                                                }}
                                                color="primary"
                                                shape="rounded"
                                                showFirstButton
                                                showLastButton
                                                siblingCount={
                                                    1
                                                }
                                                boundaryCount={
                                                    1
                                                }
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CompletedOrders;